import { defaultTelemetryPlatform } from "./default-platform.js";
import { DEFAULT_CONSOLE_PROVIDER } from "../schema/provider-core.js";
import type { ProviderConfig } from "../schema/provider.js";
import { resolveProviderDefinitions } from "../providers/resolve-definitions.js";
import { SentryClientRegistry } from "../providers/sentry/registry.js";
import type { TelemetryPlatform } from "../platforms/types.js";
import type { TelemetryModuleInput } from "./normalize.js";
import { normalizeSetupInput } from "./normalize.js";
import type { CreateTelemetryInput } from "./plan-input.js";
import type { TelemetryModule } from "../di/define-module.js";

export interface TelemetryPlan<TModule extends TelemetryModule = TelemetryModule> {
  readonly platform: TelemetryPlatform<TModule>;
  readonly moduleInput: TelemetryModuleInput;
  readonly providerConfigs: ProviderConfig[];
  readonly sentryRegistry: SentryClientRegistry;
}

export function buildTelemetryPlan<TModule extends TelemetryModule = TelemetryModule>(
  input: CreateTelemetryInput<TModule>,
): TelemetryPlan<TModule> {
  const platform = (input.platform ?? defaultTelemetryPlatform) as TelemetryPlatform<TModule>;
  const { platform: _p, providers: providerDefinitions, ...rest } = input;
  const sentryRegistry = new SentryClientRegistry();

  let providerConfigs = resolveProviderDefinitions(
    providerDefinitions,
    sentryRegistry,
  );
  if (!providerConfigs?.length) {
    providerConfigs = [{ ...DEFAULT_CONSOLE_PROVIDER }];
  }

  const moduleInput: TelemetryModuleInput = {
    ...rest,
    providers: providerConfigs,
    ssr: rest.ssr ?? platform.defaultSsr ?? false,
    captureGlobalErrors: rest.captureGlobalErrors ?? false,
  };

  return {
    platform,
    moduleInput,
    providerConfigs,
    sentryRegistry,
  };
}

export function planExpectsClient(plan: TelemetryPlan): boolean {
  const di = normalizeSetupInput(plan.moduleInput);
  if (di.client !== undefined) return true;
  if (plan.moduleInput.runtime === "server") return false;
  if (di.ssr) return true;
  if (di.server !== undefined && di.client === undefined) return false;
  return true;
}

export function planExpectsServer(plan: TelemetryPlan): boolean {
  const di = normalizeSetupInput(plan.moduleInput);
  if (di.server !== undefined) return true;
  if (plan.moduleInput.runtime === "client") return false;
  return Boolean(di.ssr);
}
